// Mobile (below md) presentation of one editable variant row — the stacked
// card the grid renders instead of the 8-column table. Pure presentation:
// it receives EXACTLY the same props as the desktop EditableVariantRowView
// (same row model, same callbacks from useEditableVariantsRows) and reuses
// the row's own building blocks — deriveVariantRowView for select options and
// per-field validation messages, PackagingCell + PackagingEditorFields for
// the packaging summary/editor, AttributesChipsCell for the chips cluster and
// VariantRowActions for the action buttons — so no editing logic is
// duplicated between the two layouts.
import { Box, ChakraSelect, Flex, HStack, SimpleGrid, Switch, Tag, Text, Tooltip } from '@elkaso-app/web-design';
import { BrandSelect } from 'components/brand-select';
import { useState } from 'react';
import { variantLabel } from '../packaging';
import { SpecTagsInput } from '../spec-tags-input';
import { PackagingCell } from './packaging-cell';
import { PackagingEditorFields } from './packaging-editor-row';
import {
  AttributesChipsCell,
  deriveVariantRowView,
  EditableVariantRowViewProps,
  VariantRowActions,
} from './variant-row';

const CardLabel = ({ children }: { children: React.ReactNode }) => (
  <Text fontSize='10.5px' fontWeight='semibold' letterSpacing='0.08em' textTransform='uppercase' color='gray.500'>
    {children}
  </Text>
);

export const VariantCard = (props: EditableVariantRowViewProps) => {
  const {
    row,
    index,
    messages,
    isPackagingDirty,
    isLastRow,
    dictionary,
    uomOptions,
    brandOptions,
    onChange,
    onEnterAppend,
    onOpenAttributesDrawer,
    isHighlighted,
    highlightSourceName,
  } = props;
  const invalid = messages.length > 0;
  const [isPackagingExpanded, setIsPackagingExpanded] = useState(false);

  const {
    stateMessages,
    brandMessages,
    stateOptions,
    selectedState,
    originOptions,
    selectedOrigin,
    brandSelectOptions,
    selectedBrand,
    packagingFields,
  } = deriveVariantRowView(row, messages, dictionary, brandOptions);

  const rawLabel = variantLabel({
    id: row.id ?? 0,
    brand: row.brand,
    spec: row.spec,
    baseUnit: row.baseUnit,
    itemUnit: row.itemUnit,
    netContent: row.netContent.trim() ? Number(row.netContent) : null,
    packUnit: row.packUnit,
    packSize: row.packSize.trim() ? Number(row.packSize) : null,
  });
  const identity = row.id === undefined && rawLabel === '#0' ? `Row ${index + 1}` : rawLabel;

  return (
    <Box
      bg={invalid ? 'red.50' : isHighlighted ? 'yellow.50' : 'white'}
      borderRadius='14px'
      border='1px solid'
      borderColor={invalid ? 'red.200' : 'gray.100'}
      p='14px'
      onKeyDown={(e) => {
        // Mirrors the desktop <Tr>: react-select calls preventDefault() on the Enter
        // that confirms a ChakraSelect option, but doesn't stopPropagation — without
        // this guard that same keystroke would also append a row.
        if (isLastRow && e.key === 'Enter' && !e.defaultPrevented) onEnterAppend();
      }}>
      <Flex align='flex-start' gap='8px'>
        <Box flex='1' minW={0}>
          <HStack spacing='1' align='center'>
            <Text fontSize='14px' fontWeight='semibold' noOfLines={2}>
              {identity}
            </Text>
            {isHighlighted && (
              <Tooltip label={`Merged from '${highlightSourceName}'`} hasArrow placement='top'>
                <Tag size='sm' colorScheme='yellow' flexShrink={0}>
                  Merged
                </Tag>
              </Tooltip>
            )}
          </HStack>
          {invalid && (
            <Box mt='2px'>
              {messages.map((message, i) => (
                <Text key={i} fontSize='12px' color='red.600'>
                  {message}
                </Text>
              ))}
            </Box>
          )}
        </Box>
        <Box flexShrink={0} textAlign='center'>
          <CardLabel>Active</CardLabel>
          <Switch
            mt='2px'
            size='md'
            isChecked={row.isActive}
            onChange={(e) => onChange(row.clientId, { isActive: e.target.checked })}
          />
        </Box>
      </Flex>

      <Box mt='10px'>
        <CardLabel>Brand</CardLabel>
        <BrandSelect
          size='sm'
          options={brandSelectOptions}
          value={selectedBrand}
          isInvalid={brandMessages.length > 0}
          placeholder='Brand'
          onBrandChange={(brand) => onChange(row.clientId, { brand })}
        />
      </Box>

      <SimpleGrid columns={2} spacing='10px' mt='10px'>
        <Box minW={0}>
          <CardLabel>Origin</CardLabel>
          <ChakraSelect
            size='sm'
            options={originOptions}
            value={selectedOrigin}
            placeholder='Origin'
            isClearable
            menuPortalTarget={document.body}
            menuPosition='fixed'
            styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
            onChange={(opt: any) => onChange(row.clientId, { origin: opt?.value ?? '' })}
          />
        </Box>
        <Box minW={0}>
          <CardLabel>State</CardLabel>
          <ChakraSelect
            size='sm'
            options={stateOptions}
            value={selectedState}
            isInvalid={stateMessages.length > 0}
            placeholder='State'
            menuPortalTarget={document.body}
            menuPosition='fixed'
            styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
            onChange={(opt: any) => onChange(row.clientId, { state: opt?.value ?? '' })}
          />
        </Box>
      </SimpleGrid>

      <Box mt='10px'>
        <CardLabel>Packaging</CardLabel>
        <Box borderWidth='1px' borderColor='gray.200' borderRadius='md' px='8px' py='6px' bg='white'>
          <PackagingCell
            fields={packagingFields}
            isExpanded={isPackagingExpanded}
            onToggle={() => setIsPackagingExpanded((v) => !v)}
          />
        </Box>
        {isPackagingExpanded && (
          <Box bg='gray.50' borderRadius='md' p='10px' mt='6px'>
            <PackagingEditorFields
              fields={packagingFields}
              uomOptions={uomOptions}
              onChange={(patch) => onChange(row.clientId, patch)}
              variantId={row.id}
              isPackagingDirty={isPackagingDirty}
            />
          </Box>
        )}
      </Box>

      <Box mt='10px'>
        <CardLabel>Spec</CardLabel>
        <SpecTagsInput size='sm' value={row.spec} onChange={(spec) => onChange(row.clientId, { spec })} />
      </Box>

      <Box mt='10px'>
        <CardLabel>Attributes</CardLabel>
        <Box mt='2px'>
          <AttributesChipsCell
            attributes={row.attributes}
            dictionary={dictionary}
            onClick={() => onOpenAttributesDrawer(row.clientId)}
          />
        </Box>
      </Box>

      <HStack mt='6px' spacing='0' flexWrap='wrap' justify='flex-end'>
        <VariantRowActions
          row={row}
          index={index}
          canMergeVariant={props.canMergeVariant}
          isTopProduct={props.isTopProduct}
          isTopProductPending={props.isTopProductPending}
          onToggleTopProduct={props.onToggleTopProduct}
          onInsert={props.onInsert}
          onRemove={props.onRemove}
          onDuplicate={props.onDuplicate}
          onMergeVariant={props.onMergeVariant}
          onDeleteVariant={props.onDeleteVariant}
          display='card'
        />
      </HStack>
    </Box>
  );
};
